/* eslint-disable react/jsx-props-no-spreading */
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { format } from 'date-fns';
import parseISO from 'date-fns/parseISO';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';

import { useThemeClassName } from '../../../contexts/ThemeContext';
import StyledButton from '../../buttons/StyledButton';
import Select from '../../fields/Select';
import LiveGameCard from '../../partials/LiveGameCard';

import styles from '../../../../styles/displays/steps/games.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    game: AppPropTypes.game,
    updateCurrentGame: PropTypes.func.isRequired,
    resetCurrentGame: PropTypes.func,
    className: PropTypes.string,
};

function GamesStep({
    page = null,
    game = null,
    updateCurrentGame = null,
    resetCurrentGame = null,
    className = null,
}) {
    const themeClassName = useThemeClassName(styles);
    const route = useUrlGenerator();
    const { games: pageGames = null, slug: display = null } = page || {};
    const { id: currentGameId = null } = game || {};

    const [selectedDate, setSelectedDate] = useState(null);
    const onDateChange = useCallback(
        (newDate) => {
            setSelectedDate(newDate);
        },
        [setSelectedDate],
    );

    const games = useMemo(
        () =>
            pageGames.map((item) => {
                const { startAt = null, ...props } = item || {};
                const start = startAt !== null ? parseISO(startAt) : null;
                return {
                    startAt,
                    date: start !== null ? format(start, 'yyyy-MM-dd') : null,
                    ...props,
                };
            }),
        [pageGames],
    );

    const sortedGames = useMemo(() => sortBy(games || [], 'id'), [games]);

    const dates = useMemo(
        () =>
            sortedGames.reduce((acc, dateGame) => {
                const { date = null } = dateGame || {};
                if (date !== null && !acc.includes(date)) {
                    acc.push(date);
                }
                return acc;
            }, []),
        [sortedGames],
    );

    const selectedGame = useMemo(
        () =>
            games.find((g) => {
                const { id = null } = g || {};
                return id === currentGameId;
            }) || null,
        [games, currentGameId],
    );

    const finalGames = useMemo(() => {
        const preGames = sortedGames.filter(({ id = null } = {}) => id !== currentGameId);
        if (selectedDate === null) {
            return preGames;
        }
        return preGames.filter((g) => {
            const { date = null } = g || {};
            return date === selectedDate;
        });
    }, [sortedGames, selectedDate]);

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className,
                },
            ])}
        >
            <div className={styles.header}>
                <div className={styles.left}>
                    <h3 className={styles.title}>
                        {selectedGame === null ? (
                            <FormattedMessage
                                defaultMessage="Select a Game"
                                description="Title for the game selection step"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Game selected"
                                description="Title for the game selection step"
                            />
                        )}
                    </h3>
                    {resetCurrentGame !== null ? (
                        <StyledButton onClick={resetCurrentGame}>
                            <FormattedMessage defaultMessage="Reset" description="Reset button" />
                        </StyledButton>
                    ) : null}
                </div>
            </div>
            <div className={styles.menu}>
                {selectedGame !== null ? (
                    <LiveGameCard
                        className={classNames([styles.item, styles.selected])}
                        game={selectedGame}
                        selected
                        withDate
                    />
                ) : null}
                {dates !== null && dates.length > 1 ? (
                    <Select
                        className={styles.select}
                        items={dates.map((date) => ({ label: date, value: date }))}
                        value={selectedDate}
                        onChange={onDateChange}
                        placeholder={
                            <FormattedMessage defaultMessage="Date" description="Placeholder" />
                        }
                    />
                ) : (
                    <hr className={styles.spacer} />
                )}
            </div>
            <div className={styles.games}>
                {(finalGames || []).map((item, index) => {
                    const { id: gameId = null } = item || {};
                    const href = route('display.step', {
                        display,
                        step: 'games',
                    });
                    return (
                        <div key={`step-${gameId}-${index + 1}`}>
                            <LiveGameCard
                                className={classNames([
                                    styles.item,
                                    { [styles.selected]: gameId === currentGameId },
                                ])}
                                game={item}
                                href={href}
                                onClick={updateCurrentGame}
                                selected={gameId === currentGameId}
                                withDate
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

GamesStep.propTypes = propTypes;

export default GamesStep;
